import { Reveal } from "../landing/Reveal";
import type { GalleryItem, GallerySection } from "../../use-cases/types";

interface NarrativeGalleryProps {
  gallery: GallerySection;
}

/**
 * Thumbnail panel for one card: the narrative's cover screen, crop-filled
 * edge to edge. `image.position` picks the slice so the headline lands in
 * frame. Without a screenshot it falls back to a navy title panel.
 */
const Thumbnail = ({ item }: { item: GalleryItem }) => (
  <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden bg-fwd-navy">
    {item.image ? (
      <img
        src={item.image.src}
        alt={item.image.alt}
        width={1020}
        height={1508}
        loading="lazy"
        style={{ objectPosition: item.image.position ?? "50% 0%" }}
        className="absolute inset-0 h-full w-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover:scale-[1.03]"
      />
    ) : (
      <div className="flex h-full flex-col justify-center gap-3 p-8">
        <span className="block h-1 w-8 rounded-full bg-fwd-red" />
        <span className="font-display text-2xl leading-tight text-fwd-white">
          {item.title}
        </span>
      </div>
    )}
  </div>
);

/**
 * Gallery of live narratives about real things ("in the wild"), in the
 * spirit of Tableau Public: each card shows the cover screen, a byline
 * crediting the author and source, and a plain description, and links out
 * to the narrative in a new tab. Used in place of `NarrativeShowcase` when a
 * vertical's narratives live in Dispatch as share links.
 */
export const NarrativeGallery = ({ gallery }: NarrativeGalleryProps) => (
  <section
    aria-labelledby="narrative-gallery-heading"
    className="bg-fwd-soft pt-12 md:pt-16 pb-24 md:pb-32 px-6 md:px-16 lg:px-24"
  >
    <div className="max-w-5xl mx-auto">
      <Reveal>
        <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase block mb-4">
          {gallery.eyebrow}
        </span>
      </Reveal>
      <Reveal delay={0.05}>
        <h2
          id="narrative-gallery-heading"
          className="font-display text-[clamp(1.8rem,3vw,2.6rem)] font-normal leading-tight text-fwd-ink mb-5 max-w-3xl"
        >
          {gallery.headline}
        </h2>
      </Reveal>
      {gallery.body && (
        <Reveal delay={0.1}>
          <p className="font-serif text-fwd-ink/75 text-lg leading-relaxed max-w-2xl">
            {gallery.body}
          </p>
        </Reveal>
      )}

      <ul className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
        {gallery.items.map((item, i) => (
          <li key={item.href} className="flex min-w-0">
            <Reveal delay={Math.min(i * 0.1, 0.3)} className="flex w-full">
              <article className="group flex w-full flex-col overflow-hidden rounded-xl border-2 border-fwd-ink bg-white shadow-[4px_4px_0_#111118]">
                {/* The CTA below is the card's one tab stop; the thumbnail
                    is a mouse/touch shortcut to the same place. */}
                <a
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-hidden="true"
                  tabIndex={-1}
                  className="block border-b-2 border-fwd-ink"
                >
                  <Thumbnail item={item} />
                </a>
                <div className="flex flex-1 flex-col p-6 md:p-7">
                  <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase mb-3">
                    {item.label}
                  </span>
                  <h3 className="font-display text-xl md:text-[1.45rem] leading-snug text-fwd-ink mb-2">
                    {item.title}
                  </h3>
                  <p className="text-fwd-mid text-sm leading-relaxed mb-4">
                    {item.byline}
                  </p>
                  <p className="font-serif text-fwd-ink/75 text-base leading-relaxed mb-6">
                    {item.description}
                  </p>
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto self-start text-sm font-semibold text-fwd-ink underline decoration-fwd-red decoration-2 underline-offset-4 hover:text-fwd-red transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-fwd-red"
                  >
                    {item.ctaLabel}
                    <span className="sr-only">
                      : {item.title} (opens in a new tab)
                    </span>
                    &nbsp;<span aria-hidden="true">&rarr;</span>
                  </a>
                </div>
              </article>
            </Reveal>
          </li>
        ))}
      </ul>
    </div>
  </section>
);
